// components/empresa/EmpresaTable.tsx
import React, { useState, useEffect } from 'react';
import { empresaService } from '../../services/entitiesServices';
import type { Empresa } from '../../types/entities';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface EmpresaTableProps {
  onEdit?: (empresa: Empresa) => void;
  onRefresh?: () => void;
}

export const EmpresaTable: React.FC<EmpresaTableProps> = ({ onEdit, onRefresh }) => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const notifier = useNotifier();

  const loadEmpresas = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔄 Carregando empresas...');
      const response = await empresaService.list();
      console.log('📡 Resposta da API:', response);
      
      // A API retorna {success: true, empresas: Array}
      const empresasData = Array.isArray(response) ? response : 
                          (response as any).empresas || response.data || [];
      console.log('📋 Empresas processadas:', empresasData);
      
      setEmpresas(empresasData);
    } catch (err) {
      console.error('❌ Erro ao carregar empresas:', err);
      
      // Mock de dados para desenvolvimento quando backend não está disponível
      if (err instanceof Error && err.message.includes('Erro de conexão')) {
        console.log('🔧 Backend não disponível, usando dados mock...');
        const mockEmpresas: Empresa[] = [
          {
            id: '1',
            cnpj: '12345678000195',
            nome: 'AIXA Tecnologia LTDA',
            email: 'contato@aixa.com.br',
            telefone: '(11) 99999-9999',
            situacao: 'ativo',
            validadeLicenca: '2025-12-31T23:59:59.000Z',
            criadoEm: '2024-01-15T10:30:00.000Z',
            atualizadoEm: '2024-10-07T08:15:00.000Z',
            grupoId: '1'
          },
          {
            id: '2',
            cnpj: '98765432000187',
            nome: 'Tech Solutions Corretora',
            email: 'admin@techsolutions.com.br',
            telefone: '(21) 88888-8888',
            situacao: 'ativo',
            validadeLicenca: '2025-06-30T23:59:59.000Z',
            criadoEm: '2024-02-20T14:45:00.000Z',
            atualizadoEm: '2024-09-15T16:20:00.000Z',
            grupoId: '2'
          },
          {
            id: '3',
            cnpj: '11122233000144',
            nome: 'Seguros Premium SA',
            email: 'info@segurospremium.com.br',
            telefone: '(31) 77777-7777',
            situacao: 'inativo',
            validadeLicenca: '2024-12-31T23:59:59.000Z',
            criadoEm: '2024-03-10T09:00:00.000Z',
            atualizadoEm: '2024-08-22T11:30:00.000Z',
          }
        ];
        setEmpresas(mockEmpresas);
        setError('⚠️ Usando dados de demonstração (backend não conectado)');
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao carregar empresas');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (onRefresh) {
      loadEmpresas();
    }
  }, [onRefresh]);

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setConfirmOpen(false);
    try {
      await empresaService.delete(deleteTargetId);
      notifier.show({ message: 'Empresa excluída com sucesso', severity: 'success' });
      await loadEmpresas();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir empresa');
    } finally {
      setDeleteTargetId(null);
    }
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj) return '-';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-xs" style={{ color: '#9E9E9E' }}>Carregando empresas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="p-3 rounded-md bg-red-50">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border rounded-lg" style={{ borderColor: '#C3B4DE' }}>
          <thead>
            <tr style={{ backgroundColor: '#F4EFFA' }}>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Nome
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                CNPJ
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Email
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Telefone
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Situação
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Validade Licença
              </th>
              <th className="border px-4 py-3 text-center text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {empresas.length === 0 ? (
              <tr>
                <td colSpan={7} className="border px-4 py-8 text-center text-xs" style={{ color: '#9E9E9E', borderColor: '#C3B4DE' }}>
                  Nenhuma empresa cadastrada
                </td>
              </tr>
            ) : (
              empresas.map((empresa) => (
                <tr key={empresa.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                    {empresa.nome}
                  </td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                    {formatCNPJ(empresa.cnpj)}
                  </td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                    {empresa.email}
                  </td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                    {empresa.telefone || '-'}
                  </td>
                  <td className="border px-4 py-3 text-xs" style={{ borderColor: '#C3B4DE' }}>
                    <span 
                      className="px-2 py-1 text-xs font-medium rounded-full"
                      style={{
                        backgroundColor: empresa.situacao === 'ativo' ? '#DEF7EC' : '#FEF3C7',
                        color: empresa.situacao === 'ativo' ? '#047857' : '#92400E'
                      }}
                    >
                      {empresa.situacao}
                    </span>
                  </td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                    {formatDate(empresa.validadeLicenca || '')}
                  </td>
                  <td className="border px-4 py-3 text-center" style={{ borderColor: '#C3B4DE' }}>
                    <div className="flex justify-center space-x-2">
                      {onEdit && (
                        <button
                          onClick={() => onEdit(empresa)}
                          className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: '#7A61B2' }}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(empresa.id)}
                        className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: '#DC2626' }}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {empresas.length > 0 && (
        <div className="text-xs" style={{ color: '#9E9E9E' }}>
          Total: {empresas.length} empresa{empresas.length !== 1 ? 's' : ''}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Empresa"
        message="Tem certeza que deseja excluir esta empresa?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetId(null); }}
      />
    </div>
  );
};